/**
 * Mapeamento de tool IDs do backend Mastra pra labels humanos em
 * português. Fonte canônica do backend:
 * `TROIA-V3-BACKEND/src/modules/ai-agents/mastra/tools/categories.ts`
 *
 * Mantido como constant local no frontend (em vez de importar do types
 * package) por 2 motivos:
 *
 * 1. **Rótulos são UX-only** — o backend só precisa do ID da tool; os
 *    nomes amigáveis são uma decoração de apresentação. Não faz sentido
 *    acoplar ao contrato cross-service.
 *
 * 2. **Evita bugs de bundling/HMR** com o `.tgz` local do types package
 *    durante desenvolvimento (mesmo padrão usado em
 *    `AGENT_CATEGORY_OPTIONS` no `AgentFormPage.tsx`).
 *
 * ⚠️ Ao adicionar uma tool nova no backend, atualizar este array também.
 * A `TOOL_OPTIONS` é a lista que aparece no autocomplete de cenários de
 * teste (campos `expectedTools` / `forbiddenTools`).
 */

export interface ToolOption {
  /** ID interno da tool — valor persistido nos cenários */
  value: string;
  /** Label amigável mostrado no chip e no dropdown de autocomplete */
  label: string;
  /** Descrição curta pra aparecer abaixo do label no dropdown */
  description?: string;
  /** Grupo semântico pra organizar o dropdown */
  group: 'Mensagens' | 'Leads' | 'Agendamento' | 'Contato' | 'Atribuição' | 'Busca' | 'Widget';
}

/**
 * Catálogo completo de tools built-in do TroiaChat (21 tools).
 *
 * Sincronizado com `TOOL_CATEGORIES` em
 * `TROIA-V3-BACKEND/src/modules/ai-agents/mastra/tools/categories.ts`.
 */
export const TOOL_OPTIONS: ToolOption[] = [
  // ─── Mensagens ─────────────────────────────────────────────────────
  {
    value: 'send_text_message',
    label: 'Enviar mensagem de texto',
    description: 'Responde ao cliente com texto escrito',
    group: 'Mensagens',
  },
  {
    value: 'send_audio_message',
    label: 'Enviar mensagem de áudio',
    description: 'Responde com áudio gerado por voz IA',
    group: 'Mensagens',
  },
  {
    value: 'send_media_message',
    label: 'Enviar mídia (imagem/vídeo/arquivo)',
    description: 'Anexa foto, vídeo ou documento na resposta',
    group: 'Mensagens',
  },

  // ─── Leads ─────────────────────────────────────────────────────────
  {
    value: 'create_lead',
    label: 'Criar novo lead',
    description: 'Registra um novo lead no CRM',
    group: 'Leads',
  },
  {
    value: 'update_lead',
    label: 'Atualizar lead',
    description: 'Edita informações de um lead existente',
    group: 'Leads',
  },

  // ─── Agendamento ───────────────────────────────────────────────────
  {
    value: 'check_calendar_availability',
    label: 'Verificar disponibilidade na agenda',
    description: 'Consulta horários livres antes de agendar',
    group: 'Agendamento',
  },
  {
    value: 'schedule_meeting',
    label: 'Agendar reunião',
    description: 'Cria um novo evento na agenda',
    group: 'Agendamento',
  },
  {
    value: 'update_scheduled_meeting',
    label: 'Atualizar reunião agendada',
    description: 'Edita data/hora de uma reunião existente',
    group: 'Agendamento',
  },
  {
    value: 'cancel_scheduled_meeting',
    label: 'Cancelar reunião',
    description: 'Remove uma reunião da agenda',
    group: 'Agendamento',
  },

  // ─── Contato ───────────────────────────────────────────────────────
  {
    value: 'update_contact_personal_info',
    label: 'Atualizar dados do contato',
    description: 'Edita nome, email ou telefone do contato',
    group: 'Contato',
  },
  {
    value: 'add_tags',
    label: 'Adicionar tags ao contato',
    description: 'Marca o contato com uma ou mais tags',
    group: 'Contato',
  },
  {
    value: 'remove_tags',
    label: 'Remover tags do contato',
    description: 'Retira tags previamente atribuídas',
    group: 'Contato',
  },

  // ─── Atribuição ────────────────────────────────────────────────────
  {
    value: 'transfer_to_human',
    label: 'Transferir para atendente humano',
    description: 'Passa a conversa para um operador',
    group: 'Atribuição',
  },
  {
    value: 'transfer_to_team',
    label: 'Transferir para equipe',
    description: 'Encaminha a conversa para um time específico',
    group: 'Atribuição',
  },

  // ─── Busca (Knowledge Bases) ───────────────────────────────────────
  {
    value: 'search_documents',
    label: 'Buscar em documentos',
    description: 'Consulta a base de documentos do agente',
    group: 'Busca',
  },
  {
    value: 'search_products',
    label: 'Buscar produtos',
    description: 'Consulta o catálogo de produtos',
    group: 'Busca',
  },
  {
    value: 'search_services',
    label: 'Buscar serviços',
    description: 'Consulta o catálogo de serviços',
    group: 'Busca',
  },
  {
    value: 'search_properties',
    label: 'Buscar imóveis',
    description: 'Consulta a base de imóveis',
    group: 'Busca',
  },
  {
    value: 'search_vehicles',
    label: 'Buscar veículos',
    description: 'Consulta a base de veículos',
    group: 'Busca',
  },

  // ─── Widget (TroiaChat website builder) ────────────────────────────
  {
    value: 'click',
    label: 'Clicar em elemento',
    description: 'Interage com um botão do widget',
    group: 'Widget',
  },
  {
    value: 'set_input_value',
    label: 'Preencher campo de formulário',
    description: 'Digita valor num input do widget',
    group: 'Widget',
  },
  {
    value: 'route',
    label: 'Navegar para página',
    description: 'Redireciona o widget para outra URL',
    group: 'Widget',
  },
];

/**
 * Lookup O(1) de tool ID → label. Retorna o próprio ID se não houver
 * mapping (ex: custom actions criadas pelo tenant) — garantindo que a
 * UI nunca quebre nem mostre nada vazio.
 */
const TOOL_LABEL_MAP: Record<string, string> = TOOL_OPTIONS.reduce(
  (acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Retorna o label amigável de uma tool. Fallback = o próprio ID,
 * garantindo render sempre. Útil em:
 * - Chips no `ScenarioFormModal`
 * - Badges no `TraceDetailDrawer`
 * - Qualquer render que receba tool IDs do backend.
 */
export function getToolLabel(toolId: string): string {
  return TOOL_LABEL_MAP[toolId] ?? toolId;
}
